import { faLaptop } from '@fortawesome/pro-light-svg-icons/faLaptop';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import { WebsiteUser } from '@propertypal/shared/src/types/websiteUser';
import getSellingAgent from '@propertypal/shared/src/utils/property/getSellingAgent';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import { RowAlignCenter } from '@propertypal/web-ui/src/layout';
import WhiteModal from '@propertypal/web-ui/src/modal/WhiteModal';
import PropertyAgentBar from '@propertypal/web-ui/src/property/PropertyAgentBar';
import { MidHeading, Text } from '@propertypal/web-ui/src/typography';
import React, { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';
import EnquiryForm from '../my/enquiries/EnquiryForm';
import { Icon, FooterLink, Header, AgentHeader, AgentLogo } from './EnquiryModal.style';
import ValuationRequestForm from './ValuationRequestForm';

interface Props {
  show: boolean;
  property: Property;
  onSuccess?: (leadId: number) => void;
  onClose: () => void;
  accountNumber?: string;
  registerInterestModal?: boolean;
  requestValuationModal?: boolean;
  loggedIn: boolean;
  hideHeader?: boolean;
  loggedInUser?: WebsiteUser;
}

const EnquiryModal: FunctionComponent<Props> = (props) => {
  const sellingAgent = getSellingAgent(props.property);
  const closeRef = useRef<HTMLButtonElement>(null);
  const showTopBanner = sellingAgent?.enhancedBranding && !props.registerInterestModal;
  const theme = useTheme();

  const title = useMemo(() => {
    if (props.hideHeader) return undefined;
    if (props.registerInterestModal) return 'Register Your Interest';
    if (props.requestValuationModal) return 'Request a Valuation';
    return 'Email the Agent';
  }, [props.hideHeader, props.registerInterestModal, props.requestValuationModal]);

  const onSuccess = (accountNumber: string, leadId?: number) => {
    trackGaEvent(props.requestValuationModal ? 'register_interest' : 'send_enquiry', {
      page: props.property.type === 'development' ? 'DDP' : 'PDP',
      account_number: accountNumber,
    });

    if (leadId && props.onSuccess) {
      props.onSuccess(leadId);
    }
  };

  useEffect(() => {
    if (props.show) {
      closeRef.current?.focus();
    }
  }, [props.show]);

  const header = (
    <Header data-testid="enquiry-modal">
      {showTopBanner && (
        <AgentHeader>
          <PropertyAgentBar property={props.property} />
        </AgentHeader>
      )}

      <RowAlignCenter>
        {!props.registerInterestModal && sellingAgent?.logo && !sellingAgent?.enhancedBranding && (
          <AgentLogo src={sellingAgent.logo.standard} />
        )}
      </RowAlignCenter>
    </Header>
  );

  const footer = (
    <>
      {!props.registerInterestModal && sellingAgent?.websiteUrl && (
        <FooterLink data-testid="websiteLink" href={sellingAgent.websiteUrl} target="_blank" rel="nofollow">
          <Icon>
            <FontAwesomeIcon color={theme.white} icon={faLaptop} size={20} />
          </Icon>

          <Text fontSize={10} style={{ marginLeft: '10px', color: theme.white }}>
            Visit {sellingAgent.organisation} Website
          </Text>
        </FooterLink>
      )}
    </>
  );

  return (
    <WhiteModal
      containerStyle={{ paddingTop: 30 }}
      show={props.show}
      onClose={props.onClose}
      header={header}
      title={title}
      footer={footer}
      closeRef={closeRef}
      maxWidth={props.requestValuationModal || props.registerInterestModal ? 780 : 580}
    >
      {props.registerInterestModal && (
        <MidHeading mb={15} align="center" fontSize={18}>
          <b>By registering your interest</b> you&apos;ll join an exclusive group of home-searchers first to hear about
          the latest development updates and be ahead of the competition with the opportunity to reserve one as soon as
          they become available
        </MidHeading>
      )}

      {props.requestValuationModal ? (
        <ValuationRequestForm
          property={props.property}
          onSuccess={onSuccess}
          initialAccountNumber={props.accountNumber}
        />
      ) : (
        <EnquiryForm
          property={props.property}
          onSuccess={onSuccess}
          initialAccountNumber={props.accountNumber}
          registerInterestModal={props.registerInterestModal}
          loggedIn={props.loggedIn}
          loggedInUser={props.loggedInUser}
        />
      )}
    </WhiteModal>
  );
};

export default EnquiryModal;
